import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { GridColDef } from '@mui/x-data-grid/models';
import { Box, IconButton, Tooltip, Button, Badge } from '@mui/material';
import { Add, Search, Refresh, GetApp } from '@mui/icons-material';
import { SearchQuotationRequestsRequest, QuotationRequestResponse, ACLAuthorizationRequest } from '@/api/bol';
import { parseJsonWithDates } from '@/utils/jsonUtils';
import { 
  PageLayout, 
  DataGridContainer, 
  JsonDetailPanel,
  SearchPanel,
  createQuotationRequestColumns,
  defaultQuotationRequestColumnVisibilityModel,
  QuotationRequestFormDialog,
  QuotationRequestDeleteDialog,
  GenericCSVExportDialog,
} from '@/components';
import { 
  useDataGridSelection, 
  usePagination, 
  useSnackbarNotification,
  useQuotationRequests,
  useStableRowId,
  usePermissions,
  useLogisticsObjectSearchUrl,
  getActionTooltip
} from '@/hooks';
import { exportToCSV, generateCSVFilename } from '@/utils/csvExport';
import { DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY } from '@/utils/searchDefaults';
import { getDefaultSavedQuery } from '@/utils/savedQueriesStorage';

const QuotationRequestsPage: React.FC = () => {
  const { can } = usePermissions();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // Use custom hooks
  const {
    quotationRequests,
    loading,
    selectedQuotationRequest,
    setSelectedQuotationRequest,
    fetchQuotationRequests,
    createQuotationRequest,
    deleteQuotationRequest,
    total
  } = useQuotationRequests();

  const { 
    selectedItem: selectedQuotationRequestForDetail, 
    selectedRowId,
    handleRowSelectionChange,
    handleCloseDetailPanel 
  } = useDataGridSelection<QuotationRequestResponse>({ 
    data: quotationRequests, 
    getItemId: (item) => item.id || '' 
  });

  const {
    pagination,
    handlePaginationModelChange,
  } = usePagination({
    onPageChange: (page, pageSize) => {
      void fetchQuotationRequests(page, pageSize, currentSearchQuery);
    }
  });

  const {
    snackbarOpen,
    snackbarMessage,
    snackbarLink,
    snackbarType,
    showSuccess,
    showError,
    closeSnackbar
  } = useSnackbarNotification();

  const {
    getSearchQueryFromUrl,
    updateSearchQueryInUrl,
    copyShareableSearchLink,
  } = useLogisticsObjectSearchUrl({ queryParamName: 'search' });

  // Search panel state
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  const initialSearchQuery = useMemo<SearchQuotationRequestsRequest>(
    () => {
      const searchQueryFromUrl = getSearchQueryFromUrl() as SearchQuotationRequestsRequest | undefined;

      return searchQueryFromUrl
        || getDefaultSavedQuery('quotation-request')?.query as SearchQuotationRequestsRequest
        || DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY;
    },
    [getSearchQueryFromUrl],
  );
  const [currentSearchQuery, setCurrentSearchQuery] = useState<SearchQuotationRequestsRequest>(initialSearchQuery);

  const hasAppliedQueryOrSort = useMemo(() => {
    const hasQueryContent = !!currentSearchQuery?.query
      && typeof currentSearchQuery.query === 'object'
      && Object.keys(currentSearchQuery.query).length > 0;
    const hasSortContent = Array.isArray(currentSearchQuery?.sort)
      && currentSearchQuery.sort.length > 0;

    return hasQueryContent || hasSortContent;
  }, [currentSearchQuery]);
  
  // Tracks currentnt column order and visibility for CSV export
  const displayColumnsRef = useRef<{ columns: GridColDef[]; visibilityModel: Record<string, boolean> }>({
    columns: [],
    visibilityModel: defaultQuotationRequestColumnVisibilityModel,
  });

  const handleDisplayColumnsChange = useCallback((cols: GridColDef[], visibilityModel: Record<string, boolean>) => {
    displayColumnsRef.current = { columns: cols, visibilityModel };
  }, []);

  const getRowId = useStableRowId<QuotationRequestResponse>('quotation-request');

  // Event handlers
  const handleCreateQuotationRequest = async (data: { memberIdentifier: string; quotationRequestJson: string; aclAuthorizations?: ACLAuthorizationRequest[] }) => {
    try {
      // Validate required fields
      if (!data.memberIdentifier?.trim()) {
        throw new Error('Member identifier is required');
      }
      
      if (!data.quotationRequestJson?.trim()) {
        throw new Error('Quotation Request JSON is required');
      }

      // Parse and validate JSON
      let quotationRequestData;
      try {
        quotationRequestData = parseJsonWithDates(data.quotationRequestJson);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        throw new Error('Invalid JSON format. Please check your JSON syntax.');
      }

      // Validate parsed data structure
      if (!quotationRequestData || typeof quotationRequestData !== 'object') {
        throw new Error('Quotation Request data must be a valid JSON object');
      }

      await createQuotationRequest(quotationRequestData, data.memberIdentifier, data.aclAuthorizations);
      setCreateDialogOpen(false);
      fetchQuotationRequests(pagination.page, pagination.pageSize, currentSearchQuery);
    } catch (error) {
      console.error('Quotation Request creation validation error:', error);
      throw error; // Re-throw to let the dialog handle the error display
    }
  };

  const handleDeleteQuotationRequest = async () => {
    if (selectedQuotationRequest?.id) {
      await deleteQuotationRequest(selectedQuotationRequest.id);
      setSelectedQuotationRequest(null);
      setDeleteDialogOpen(false);
      fetchQuotationRequests(pagination.page, pagination.pageSize, currentSearchQuery);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDeleteClick = (quotationRequest: any) => {
    setSelectedQuotationRequest(quotationRequest);
    setDeleteDialogOpen(true);
  };

  const handleRefresh = () => {
    fetchQuotationRequests(pagination.page, pagination.pageSize, currentSearchQuery);
  };

  const handleSearch = async (searchData: unknown) => {
    console.log('Executing search with data:', searchData);
    const searchBody = searchData as SearchQuotationRequestsRequest;
    setCurrentSearchQuery(searchBody);
    const isSearchSuccessful = await fetchQuotationRequests(0, pagination.pageSize, searchBody); // Reset to first page on search

    if (isSearchSuccessful) {
      updateSearchQueryInUrl(searchBody);
    }
  };

  const handleSearchReset = async () => {
    const defaultSearchQuery = (getDefaultSavedQuery('quotation-request')?.query as SearchQuotationRequestsRequest)
      || DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY;
    setCurrentSearchQuery(defaultSearchQuery);
    const isResetSuccessful = await fetchQuotationRequests(0, pagination.pageSize, defaultSearchQuery); // Reset to first page with default search

    if (isResetSuccessful) {
      updateSearchQueryInUrl(defaultSearchQuery);
    }
  };

  const handleCopySearchLink = useCallback(async () => {
    await copyShareableSearchLink(currentSearchQuery, {
      onSuccess: showSuccess,
      onError: showError,
    });
  }, [copyShareableSearchLink, currentSearchQuery, showError, showSuccess]);

  const handleToggleSearchPanel = () => {
    setSearchPanelOpen(!searchPanelOpen);
  };

  const handleExportCSV = (separator: string) => {
    exportToCSV({
      data: quotationRequests,
      columns: displayColumnsRef.current.columns,
      columnVisibilityModel: displayColumnsRef.current.visibilityModel,
      filename: generateCSVFilename('quotation_requests'),
      separator,
    });
  };

  const columns = createQuotationRequestColumns({
    onDelete: handleDeleteClick,
    canDelete: can.deleteLogisticsObject,
    deleteTooltip: getActionTooltip('deleteLogisticsObject')
  });

  useEffect(() => {
    fetchQuotationRequests(0, 10, initialSearchQuery);
  }, [fetchQuotationRequests, initialSearchQuery]);

  return (
    <PageLayout 
      title="Quotation Requests Management"
      selectedItemInfo={selectedQuotationRequestForDetail ? `Selected: ${selectedQuotationRequestForDetail.id}` : undefined}
      notificationOpen={snackbarOpen}
      notificationMessage={snackbarMessage}
      notificationLink={snackbarLink}
      notificationType={snackbarType}
      onNotificationClose={closeSnackbar}
      leftTitleActions={
        <>
          <Tooltip title="Refresh quotation requests list">
            <IconButton
              onClick={handleRefresh}
              size="small"
            >
              <Refresh />
            </IconButton>
          </Tooltip>
          <Tooltip title={searchPanelOpen ? "Close search panel" : "Open search panel"}>
            <IconButton
              onClick={handleToggleSearchPanel}
              color={searchPanelOpen ? "primary" : "default"}
              size="small"
            >
              <Badge color="secondary" variant="dot" invisible={!hasAppliedQueryOrSort}>
                <Search />
              </Badge>
            </IconButton>
          </Tooltip>
        </>
      }
      titleActions={
          <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<GetApp />}
            onClick={() => setExportDialogOpen(true)}
            size="medium"
          >
            Export CSV
          </Button>
          <Tooltip title={!can.createLogisticsObject ? getActionTooltip('createLogisticsObject') : ''}>
          <span>
          <Button
                variant="contained"
                size="medium"
                onClick={() => setCreateDialogOpen(true)}
                startIcon={<Add />}
              disabled={!can.createLogisticsObject}
              >
                Create QuotationRequest
              </Button>
          </span>
        </Tooltip>
        </Box>
      }
      leftPanel={searchPanelOpen ? (
        <SearchPanel
          title="Search Quotation Requests"
          onClose={() => setSearchPanelOpen(false)}
          onSearch={handleSearch}
          onReset={handleSearchReset}
          onCopyLink={handleCopySearchLink}
          loading={loading}
          logisticsObjectType="quotation-request"
          currentSearchQuery={currentSearchQuery}
        />
      ) : undefined}
      sidePanel={selectedQuotationRequestForDetail ? (
        <JsonDetailPanel
          title="QuotationRequest Details"
          data={selectedQuotationRequestForDetail}
          onClose={handleCloseDetailPanel}
          onCopyError={() => showError('Failed to copy JSON to clipboard')}
        />
      ) : undefined}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <DataGridContainer
          rows={quotationRequests}
          columns={columns}
          loading={loading}
          selectedRowId={selectedRowId}
          onRowSelectionChange={handleRowSelectionChange}
          paginationModel={pagination}
          rowCount={total}
          onPaginationModelChange={handlePaginationModelChange}
          getRowId={getRowId}
          columnVisibilityModel={defaultQuotationRequestColumnVisibilityModel}
          columnOrderStorageKey="quotation-requests-columns"
          onDisplayColumnsChange={handleDisplayColumnsChange}
        />
      </Box>

      {/* Create QuotationRequest Dialog */}
      <QuotationRequestFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateQuotationRequest}
      />

      {/* Delete Confirmation Dialog */}
      <QuotationRequestDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteQuotationRequest}
        quotationRequest={selectedQuotationRequest}
      />

      {/* CSV Export Dialog */}
      <GenericCSVExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onSubmit={handleExportCSV}
        title="Export Quotation Requests to CSV"
        entityName="quotation requests"
        defaultFilename={generateCSVFilename('quotation_requests')}
        currentPageInfo={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalRows: total,
        }}
      />

      </PageLayout>
    );
  };

export default QuotationRequestsPage;
